// SensorVisualisations.js
import React, { useState, useEffect, useMemo } from 'react';
import { Typography } from 'antd';
import moment from 'moment-timezone';
import { extractPulseEntries, calculateTotalDelta } from './doorSensorUtils';

const { Text } = Typography;

// Helper function to get last known value
const getLastKnownValue = (sensor, sensorType) => {
  if (!sensor.timelines || !sensor.timelines.length) return null;
  for (let i = sensor.timelines.length - 1; i >= 0; i--) {
    const timeline = sensor.timelines[i];
    if (timeline[sensorType]) {
      return {
        value: timeline[sensorType].value,
        unit: timeline[sensorType].unit || '',
        time: timeline[sensorType].time
      };
    }
  }
  return null;
};

// Generic Gauge Display for most sensor types
const GenericGaugeDisplay = ({ sensor, sensorType, label, minValue = 0, maxValue = 100, colorThresholds = null, compact = false }) => {
  const [animatedAngle, setAnimatedAngle] = useState(-90);

  const sensorData = getLastKnownValue(sensor, sensorType);
  const value = sensorData ? parseFloat(sensorData.value) : 0;
  const normalizedValue = Math.max(0, Math.min(100, ((value - minValue) / (maxValue - minValue)) * 100));
  const angle = -90 + (180 * normalizedValue / 100);

  useEffect(() => {
    if (!sensorData) return;
    const timeout = setTimeout(() => setAnimatedAngle(angle), 150);
    return () => clearTimeout(timeout);
  }, [angle, sensorData]);

  if (!sensorData) return null;

  const getColor = (val) => {
    if (colorThresholds) {
      for (let threshold of colorThresholds) {
        if (val < threshold.max) return threshold.color;
      }
      return colorThresholds[colorThresholds.length - 1].color;
    }
    return '#0E4128';
  };

  const color = getColor(value);

  // Compact mode for TV display
  if (compact) {
    // Dynamic font size based on value length
    const valueStr = value.toFixed(value < 10 ? 2 : value < 100 ? 1 : 0);
    const digitCount = valueStr.replace('.', '').length;
    const fontSize = digitCount > 3 ? '20px' : '24px';
    const unitSize = digitCount > 3 ? '12px' : '14px';

    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        gap: '8px'
      }}>
        <div style={{ position: 'relative', width: '80px', height: '70px' }}>
          <svg width="80" height="70" viewBox="0 0 80 70" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <linearGradient id={`gaugeGradient-${sensor.id}-${sensorType}`} x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#0E4128" stopOpacity="0.3" />
                <stop offset="50%" stopColor="#0E4128" stopOpacity="0.7" />
                <stop offset="100%" stopColor="#0E4128" />
              </linearGradient>
            </defs>

            <path d="M 15 55 A 25 25 0 0 1 65 55" fill="none" stroke="white" strokeWidth="6" strokeLinecap="round" />
            <path d="M 15 55 A 25 25 0 0 1 65 55" fill="none" stroke={`url(#gaugeGradient-${sensor.id}-${sensorType})`} strokeWidth="5" strokeLinecap="round" />

            {[-90, -45, 0, 45, 90].map((deg) => (
              <line key={deg} x1="40" y1="32" x2="40" y2="27" stroke="#999" strokeWidth="1.5" transform={`rotate(${deg} 40 55)`} />
            ))}

            <g style={{ transform: `rotate(${animatedAngle}deg)`, transformOrigin: '40px 55px', transition: 'transform 1s ease-in-out' }}>
              <line x1="40" y1="55" x2="40" y2="33" stroke={color} strokeWidth="2" strokeLinecap="round" />
              <circle cx="40" cy="33" r="2" fill={color} />
            </g>

            <circle cx="40" cy="55" r="5" fill={color} stroke="white" strokeWidth="1.5" />
          </svg>
        </div>

        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: fontSize, fontWeight: 'bold', color: color, lineHeight: 1 }}>
            {valueStr}
            <span style={{ fontSize: unitSize, marginLeft: '4px' }}>{sensorData.unit}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      marginTop: '12px',
      marginBottom: '12px',
      padding: '16px',
      backgroundColor: '#f5f7f5',
      borderRadius: '8px',
      border: '1px solid #e8e8e8'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
        <div style={{ position: 'relative', width: '100px', height: '80px', flexShrink: 0 }}>
          <svg width="100" height="80" viewBox="0 0 100 80" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <linearGradient id={`gaugeGradient-${sensor.id}-${sensorType}`} x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#0E4128" stopOpacity="0.3" />
                <stop offset="50%" stopColor="#0E4128" stopOpacity="0.7" />
                <stop offset="100%" stopColor="#0E4128" />
              </linearGradient>
            </defs>

            <path d="M 15 65 A 35 35 0 0 1 85 65" fill="none" stroke="#e0e0e0" strokeWidth="8" strokeLinecap="round" />
            <path d="M 15 65 A 35 35 0 0 1 85 65" fill="none" stroke={`url(#gaugeGradient-${sensor.id}-${sensorType})`} strokeWidth="6" strokeLinecap="round" />

            {[-90, -45, 0, 45, 90].map((deg) => (
              <line key={deg} x1="50" y1="32" x2="50" y2="26" stroke="#999" strokeWidth="2" transform={`rotate(${deg} 50 65)`} />
            ))}

            <g style={{ transform: `rotate(${animatedAngle}deg)`, transformOrigin: '50px 65px', transition: 'transform 1s ease-in-out' }}>
              <line x1="50" y1="65" x2="50" y2="33" stroke={color} strokeWidth="2.5" strokeLinecap="round" />
              <circle cx="50" cy="33" r="2.5" fill={color} />
            </g>

            <circle cx="50" cy="65" r="6" fill={color} stroke="white" strokeWidth="2" />
          </svg>
        </div>

        <div style={{ flex: 1 }}>
          <Text strong style={{ fontSize: '12px', display: 'block', color: '#666' }}>
            {label}
          </Text>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px', marginTop: '4px' }}>
            <Text strong style={{ fontSize: '28px', color: color, lineHeight: 1 }}>
              {value.toFixed(value < 10 ? 2 : value < 100 ? 1 : 0)}
            </Text>
            <Text style={{ fontSize: '14px', color: color }}>{sensorData.unit}</Text>
          </div>
          {sensorData.time && (
            <Text type="secondary" style={{ fontSize: '10px', display: 'block', marginTop: '8px' }}>
              Last updated: {sensorData.time}
            </Text>
          )}
        </div>
      </div>
    </div>
  );
};

// Temperature Thermometer Display
export const TemperatureDisplay = ({ sensor, compact = false }) => {
  const [animatedTemp, setAnimatedTemp] = useState(0);

  const tempData = getLastKnownValue(sensor, 'temperature');
  const temp = tempData ? parseFloat(tempData.value) : 0;
  const minTemp = -20;
  const maxTemp = 50;
  const normalizedTemp = Math.max(0, Math.min(100, ((temp - minTemp) / (maxTemp - minTemp)) * 100));

  useEffect(() => {
    if (!tempData) return;
    const timeout = setTimeout(() => setAnimatedTemp(normalizedTemp), 150);
    return () => clearTimeout(timeout);
  }, [normalizedTemp, tempData]);

  if (!tempData) return null;

  const color = '#0E4128';

  if (compact) {
    // Dynamic font size based on value length
    const tempStr = temp.toFixed(1);
    const digitCount = tempStr.replace('.', '').replace('-', '').length;
    const fontSize = digitCount > 3 ? '20px' : '24px';
    const unitSize = digitCount > 3 ? '12px' : '14px';

    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        gap: '8px'
      }}>
        <svg width="60" height="110" viewBox="0 0 60 110" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <linearGradient id={`tempGradient-${sensor.id}`} x1="0%" y1="100%" x2="0%" y2="0%">
              <stop offset="0%" stopColor={color} stopOpacity="0.9" />
              <stop offset="100%" stopColor={color} stopOpacity="0.6" />
            </linearGradient>
          </defs>

          <rect x="22" y="10" width="16" height="70" rx="8" fill="white" stroke="#555" strokeWidth="2" />
          <rect x="24" y={12 + (66 * (100 - animatedTemp) / 100)} width="12" height={66 * animatedTemp / 100} rx="6" fill={`url(#tempGradient-${sensor.id})`} style={{ transition: 'all 1s ease-in-out' }} />
          <circle cx="30" cy="90" r="16" fill={color} stroke="#555" strokeWidth="2" />
          <circle cx="30" cy="90" r="11" fill={color} opacity="0.8" />

          {[0, 25, 50, 75, 100].map((mark) => (
            <line key={mark} x1="40" y1={12 + (66 * (100 - mark) / 100)} x2="45" y2={12 + (66 * (100 - mark) / 100)} stroke="#999" strokeWidth="1" />
          ))}
        </svg>

        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: fontSize, fontWeight: 'bold', color: color, lineHeight: 1 }}>
            {tempStr}
            <span style={{ fontSize: unitSize, marginLeft: '4px' }}>{tempData.unit}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      marginTop: '12px',
      marginBottom: '12px',
      padding: '16px',
      backgroundColor: '#f5f7f5',
      borderRadius: '8px',
      border: '1px solid #e8e8e8'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
        <div style={{ position: 'relative', width: '60px', height: '120px', flexShrink: 0 }}>
          <svg width="60" height="120" viewBox="0 0 60 120" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <linearGradient id={`tempGradient-${sensor.id}`} x1="0%" y1="100%" x2="0%" y2="0%">
                <stop offset="0%" stopColor={color} stopOpacity="0.9" />
                <stop offset="100%" stopColor={color} stopOpacity="0.6" />
              </linearGradient>
            </defs>

            <rect x="22" y="10" width="16" height="75" rx="8" fill="white" stroke="#555" strokeWidth="2" />
            <rect x="24" y={10 + (73 * (100 - animatedTemp) / 100)} width="12" height={73 * animatedTemp / 100} rx="6" fill={`url(#tempGradient-${sensor.id})`} style={{ transition: 'all 1s ease-in-out' }} />
            <circle cx="30" cy="95" r="18" fill={color} stroke="#555" strokeWidth="2" />
            <circle cx="30" cy="95" r="12" fill={color} opacity="0.8" />

            {[0, 25, 50, 75, 100].map((mark) => (
              <line key={mark} x1="40" y1={10 + (73 * (100 - mark) / 100)} x2="45" y2={10 + (73 * (100 - mark) / 100)} stroke="#999" strokeWidth="1" />
            ))}
          </svg>
        </div>

        <div style={{ flex: 1 }}>
          <Text strong style={{ fontSize: '12px', display: 'block', color: '#666' }}>
            Temperature
          </Text>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
            <Text strong style={{ fontSize: '32px', color: color, lineHeight: 1 }}>
              {temp.toFixed(1)}
            </Text>
            <Text style={{ fontSize: '18px', color: color }}>{tempData.unit}</Text>
          </div>
          {tempData.time && (
            <Text type="secondary" style={{ fontSize: '10px', display: 'block', marginTop: '8px' }}>
              Last updated: {tempData.time}
            </Text>
          )}
        </div>
      </div>
    </div>
  );
};

// Humidity/Moisture Water Droplet Display
export const HumidityDisplay = ({ sensor, sensorType = 'humidity', compact = false }) => {
  const [animatedHumidity, setAnimatedHumidity] = useState(0);

  const humidityData = getLastKnownValue(sensor, sensorType);
  const humidity = humidityData ? parseFloat(humidityData.value) : 0;

  useEffect(() => {
    if (!humidityData) return;
    const timeout = setTimeout(() => setAnimatedHumidity(humidity), 150);
    return () => clearTimeout(timeout);
  }, [humidity, humidityData]);

  if (!humidityData) return null;

  const color = '#0E4128';

  if (compact) {
    // Dynamic font size based on value length
    const humidityStr = humidity.toFixed(1);
    const digitCount = humidityStr.replace('.', '').length;
    const fontSize = digitCount > 3 ? '20px' : '24px';
    const unitSize = digitCount > 3 ? '12px' : '14px';

    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        gap: '8px'
      }}>
        <svg width="70" height="90" viewBox="0 0 70 90" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <clipPath id={`dropletClip-${sensor.id}-${sensorType}`}>
              <path d="M 35 8 Q 27 26, 19 38 Q 10 51, 14 64 Q 19 77, 35 82 Q 51 77, 56 64 Q 60 51, 51 38 Q 43 26, 35 8 Z" />
            </clipPath>
            <linearGradient id={`humidityGradient-${sensor.id}-${sensorType}`} x1="0%" y1="100%" x2="0%" y2="0%">
              <stop offset="0%" stopColor={color} stopOpacity="0.95" />
              <stop offset="100%" stopColor={color} stopOpacity="0.6" />
            </linearGradient>
          </defs>

          <path d="M 35 8 Q 27 26, 19 38 Q 10 51, 14 64 Q 19 77, 35 82 Q 51 77, 56 64 Q 60 51, 51 38 Q 43 26, 35 8 Z" fill="white" stroke="#555" strokeWidth="2" />
          <rect x="0" y={82 - (74 * animatedHumidity / 100)} width="70" height={74 * animatedHumidity / 100} fill={`url(#humidityGradient-${sensor.id}-${sensorType})`} clipPath={`url(#dropletClip-${sensor.id}-${sensorType})`} style={{ transition: 'all 1.2s ease-in-out' }} />
        </svg>

        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: fontSize, fontWeight: 'bold', color: color, lineHeight: 1 }}>
            {humidityStr}
            <span style={{ fontSize: unitSize, marginLeft: '4px' }}>%</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      marginTop: '12px',
      marginBottom: '12px',
      padding: '16px',
      backgroundColor: '#f5f7f5',
      borderRadius: '8px',
      border: '1px solid #e8e8e8'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
        <div style={{ position: 'relative', width: '80px', height: '100px', flexShrink: 0 }}>
          <svg width="80" height="100" viewBox="0 0 80 100" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <clipPath id={`dropletClip-${sensor.id}-${sensorType}`}>
                <path d="M 40 10 Q 30 30, 20 45 Q 10 60, 15 75 Q 20 90, 40 95 Q 60 90, 65 75 Q 70 60, 60 45 Q 50 30, 40 10 Z" />
              </clipPath>
              <linearGradient id={`humidityGradient-${sensor.id}-${sensorType}`} x1="0%" y1="100%" x2="0%" y2="0%">
                <stop offset="0%" stopColor={color} stopOpacity="0.95" />
                <stop offset="100%" stopColor={color} stopOpacity="0.6" />
              </linearGradient>
            </defs>

            <path d="M 40 10 Q 30 30, 20 45 Q 10 60, 15 75 Q 20 90, 40 95 Q 60 90, 65 75 Q 70 60, 60 45 Q 50 30, 40 10 Z" fill="white" stroke="#555" strokeWidth="2" />
            <rect x="0" y={95 - (85 * animatedHumidity / 100)} width="80" height={85 * animatedHumidity / 100} fill={`url(#humidityGradient-${sensor.id}-${sensorType})`} clipPath={`url(#dropletClip-${sensor.id}-${sensorType})`} style={{ transition: 'all 1.2s ease-in-out' }} />
          </svg>
        </div>

        <div style={{ flex: 1 }}>
          <Text strong style={{ fontSize: '12px', display: 'block', color: '#666' }}>
            {sensorType === 'moisture' ? 'Soil Moisture' : 'Humidity'}
          </Text>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px', marginTop: '4px' }}>
            <Text strong style={{ fontSize: '28px', color: color, lineHeight: 1 }}>
              {humidity.toFixed(1)}
            </Text>
            <Text style={{ fontSize: '16px', color: color }}>%</Text>
          </div>
          {humidityData.time && (
            <Text type="secondary" style={{ fontSize: '10px', display: 'block', marginTop: '8px' }}>
              Last updated: {humidityData.time}
            </Text>
          )}
        </div>
      </div>
    </div>
  );
};

// Moisture Display (reuses HumidityDisplay)
export const MoistureDisplay = ({ sensor, compact = false }) => {
  return <HumidityDisplay sensor={sensor} sensorType="moisture" compact={compact} />;
};

// Pressure Gauge Display
export const PressureDisplay = ({ sensor, compact = false }) => {
  const colorThresholds = [
    { max: 980, color: '#4A90E2' },
    { max: 1013, color: '#50C878' },
    { max: Infinity, color: '#DC143C' }
  ];

  return (
    <GenericGaugeDisplay
      sensor={sensor}
      sensorType="pressure"
      label="Atmospheric Pressure"
      minValue={950}
      maxValue={1050}
      colorThresholds={colorThresholds}
      compact={compact}
    />
  );
};

// Battery/Voltage Display - Now using gauge
export const VoltageDisplay = ({ sensor, compact = false }) => {
  const colorThresholds = [
    { max: 1, color: '#0E4128' },
    { max: 2.5, color: '#0E4128' },
    { max: Infinity, color: '#0E4128' }
  ];

  return (
    <GenericGaugeDisplay
      sensor={sensor}
      sensorType="voltage"
      label="Voltage Level"
      minValue={0}
      maxValue={5}
      colorThresholds={colorThresholds}
      compact={compact}
    />
  );
};

// Conductivity Display - Now using gauge
export const ConductivityDisplay = ({ sensor, compact = false }) => {
  const colorThresholds = [
    { max: 600, color: '#0E4128' },
    { max: 1400, color: '#0E4128' },
    { max: Infinity, color: '#0E4128' }
  ];

  return (
    <GenericGaugeDisplay
      sensor={sensor}
      sensorType="conductivity"
      label="Electrical Conductivity"
      minValue={0}
      maxValue={2000}
      colorThresholds={colorThresholds}
      compact={compact}
    />
  );
};

// Relay Display - Now using generic gauge
export const RelayDisplay = ({ sensor, compact = false }) => {
  const relayData = getLastKnownValue(sensor, 'relay');

  if (!relayData) return null;

  const isOn = relayData.value === 1 || relayData.value === '1' || relayData.value === 'on';
  const value = isOn ? 1 : 0;

  const colorThresholds = [
    { max: 0.5, color: '#999' },
    { max: Infinity, color: '#50C878' }
  ];

  return (
    <GenericGaugeDisplay
      sensor={{ ...sensor, timelines: [{ relay: { value, unit: isOn ? 'ON' : 'OFF', time: relayData.time } }] }}
      sensorType="relay"
      label="Relay Status"
      minValue={0}
      maxValue={1}
      colorThresholds={colorThresholds}
      compact={compact}
    />
  );
};

// Distance/Height Display - Vertical ruler (INVERTED - lower mm = more green)
export const DistanceDisplay = ({ sensor, sensorType = 'distance', compact = false }) => {
  const [animatedDistance, setAnimatedDistance] = useState(0);

  const distanceData = getLastKnownValue(sensor, sensorType);
  const distance = distanceData ? parseFloat(distanceData.value) : 0;
  const maxDistance = 5000;
  // INVERTED: Lower distance = higher percentage (more green)
  const percentage = Math.max(0, Math.min(100, ((maxDistance - distance) / maxDistance) * 100));

  // Convert units: mm -> cm (if > 1000) -> m (if cm > 1000)
  let displayValue = distance;
  let displayUnit = distanceData?.unit || 'mm';

  if (distanceData && distanceData.unit && distanceData.unit.toLowerCase() === 'mm') {
    if (distance >= 1000000) {
      // Convert to meters
      displayValue = distance / 1000;
      displayUnit = 'm';
    } else if (distance >= 1000) {
      // Convert to centimeters
      displayValue = distance / 10;
      displayUnit = 'cm';
    }
  }

  useEffect(() => {
    if (!distanceData) return;
    const timeout = setTimeout(() => setAnimatedDistance(percentage), 150);
    return () => clearTimeout(timeout);
  }, [percentage, distanceData]);

  if (!distanceData) return null;

  const color = '#0E4128';

  if (compact) {
    // Dynamic font size based on value length
    const distanceStr = displayValue >= 1000 ? displayValue.toFixed(1) : displayValue.toFixed(0);
    const digitCount = distanceStr.replace('.', '').length;
    let fontSize, unitSize;
    if (digitCount > 5) {
      fontSize = '14px';
      unitSize = '9px';
    } else if (digitCount > 4) {
      fontSize = '16px';
      unitSize = '10px';
    } else if (digitCount > 3) {
      fontSize = '18px';
      unitSize = '11px';
    } else {
      fontSize = '24px';
      unitSize = '14px';
    }

    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        gap: '8px'
      }}>
        <div style={{ position: 'relative', width: '40px', height: '60px' }}>
          <svg width="40" height="60" viewBox="0 0 40 60" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <linearGradient id={`distanceGradient-${sensor.id}-${sensorType}`} x1="0%" y1="100%" x2="0%" y2="0%">
                <stop offset="0%" stopColor={color} stopOpacity="0.9" />
                <stop offset="100%" stopColor={color} stopOpacity="0.5" />
              </linearGradient>
            </defs>

            <rect x="12" y="6" width="16" height="48" rx="2" fill="white" stroke="#555" strokeWidth="1.5" />
            {/* Fill from bottom up based on inverted percentage */}
            <rect
              x="13.5"
              y={7.5 + (45 * (100 - animatedDistance) / 100)}
              width="13"
              height={45 * animatedDistance / 100}
              rx="1"
              fill={`url(#distanceGradient-${sensor.id}-${sensorType})`}
              style={{ transition: 'all 1s ease-in-out' }}
            />

            {[0, 25, 50, 75, 100].map((mark) => (
              <line key={mark} x1="28" y1={7.5 + (45 * mark / 100)} x2="32" y2={7.5 + (45 * mark / 100)} stroke="#555" strokeWidth="1" />
            ))}

            {/* Arrow points to current level (inverted) - pointing right */}
            <path
              d={`M 11 ${7.5 + (45 * (100 - animatedDistance) / 100)} L 8 ${7.5 + (45 * (100 - animatedDistance) / 100) - 2.5} L 8 ${7.5 + (45 * (100 - animatedDistance) / 100) + 2.5} Z`}
              fill={color}
              style={{ transition: 'all 1s ease-in-out' }}
            />
          </svg>
        </div>

        <div style={{ textAlign: 'left', maxWidth: '80px', overflow: 'hidden' }}>
          <div style={{ fontSize: fontSize, fontWeight: 'bold', color: color, lineHeight: 1, whiteSpace: 'nowrap' }}>
            {distanceStr}
            <span style={{ fontSize: unitSize, marginLeft: '2px' }}>{displayUnit}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      marginTop: '12px',
      marginBottom: '12px',
      padding: '16px',
      backgroundColor: '#f5f7f5',
      borderRadius: '8px',
      border: '1px solid #e8e8e8'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
        <div style={{ position: 'relative', width: '80px', height: '100px', flexShrink: 0 }}>
          <svg width="80" height="100" viewBox="0 0 80 100" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <linearGradient id={`distanceGradient-${sensor.id}-${sensorType}`} x1="0%" y1="100%" x2="0%" y2="0%">
                <stop offset="0%" stopColor={color} stopOpacity="0.9" />
                <stop offset="100%" stopColor={color} stopOpacity="0.5" />
              </linearGradient>
            </defs>

            <rect x="25" y="10" width="30" height="80" rx="3" fill="white" stroke="#555" strokeWidth="2" />
            {/* Fill from bottom up based on inverted percentage */}
            <rect
              x="27"
              y={12 + (76 * (100 - animatedDistance) / 100)}
              width="26"
              height={76 * animatedDistance / 100}
              rx="2"
              fill={`url(#distanceGradient-${sensor.id}-${sensorType})`}
              style={{ transition: 'all 1s ease-in-out' }}
            />

            {[0, 20, 40, 60, 80, 100].map((mark) => (
              <line key={mark} x1="55" y1={12 + (76 * mark / 100)} x2="62" y2={12 + (76 * mark / 100)} stroke="#555" strokeWidth="1.5" />
            ))}

            {/* Arrow points to current level (inverted) - pointing right */}
            <path
              d={`M 22 ${12 + (76 * (100 - animatedDistance) / 100)} L 15 ${12 + (76 * (100 - animatedDistance) / 100) - 5} L 15 ${12 + (76 * (100 - animatedDistance) / 100) + 5} Z`}
              fill={color}
              style={{ transition: 'all 1s ease-in-out' }}
            />
          </svg>
        </div>

        <div style={{ flex: 1 }}>
          <Text strong style={{ fontSize: '12px', display: 'block', color: '#666' }}>
            {sensorType === 'height' ? 'Height' : 'Distance'}
          </Text>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px', marginTop: '4px' }}>
            <Text strong style={{ fontSize: '28px', color: color, lineHeight: 1 }}>
              {displayValue >= 1000 ? displayValue.toFixed(1) : displayValue.toFixed(0)}
            </Text>
            <Text style={{ fontSize: '14px', color: color }}>{displayUnit}</Text>
          </div>
          {distanceData.time && (
            <Text type="secondary" style={{ fontSize: '10px', display: 'block', marginTop: '8px' }}>
              Last updated: {distanceData.time}
            </Text>
          )}
        </div>
      </div>
    </div>
  );
};

// Height Display (reuses DistanceDisplay)
export const HeightDisplay = ({ sensor, compact = false }) => {
  return <DistanceDisplay sensor={sensor} sensorType="height" compact={compact} />;
};

export const WindSpeedDisplay = ({ sensor, compact = false }) => {
  const colorThresholds = [
    { max: 5, color: '#50C878' },   // Light breeze
    { max: 15, color: '#50C878' },  // Moderate
    { max: 25, color: '#50C878' },  // Strong
    { max: Infinity, color: '#50C878' }  // Gale
  ];

  return (
    <GenericGaugeDisplay
      sensor={sensor}
      sensorType="wind_speed"
      label="Wind Speed"
      minValue={0}
      maxValue={40}
      colorThresholds={colorThresholds}
      compact={compact}
    />
  );
};

// Wind Direction Display - Compass style
export const WindDirectionDisplay = ({ sensor, compact = false }) => {
  const [animatedAngle, setAnimatedAngle] = useState(0);

  const windData = getLastKnownValue(sensor, 'wind_direction');
  const degrees = windData ? parseFloat(windData.value) : 0;

  useEffect(() => {
    if (!windData) return;
    const timeout = setTimeout(() => setAnimatedAngle(degrees), 150);
    return () => clearTimeout(timeout);
  }, [degrees, windData]);

  if (!windData) return null;

  const color = '#0E4128';

  // Convert degrees to cardinal direction
  const getCardinalDirection = (deg) => {
    const directions = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
    const index = Math.round(((deg % 360) / 45)) % 8;
    return directions[index];
  };

  if (compact) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        gap: '8px'
      }}>
        <div style={{ position: 'relative', width: '80px', height: '80px' }}>
          <svg width="80" height="80" viewBox="0 0 80 80" xmlns="http://www.w3.org/2000/svg">
            {/* Compass circle */}
            <circle cx="40" cy="40" r="35" fill="white" stroke="#555" strokeWidth="2" />
            <circle cx="40" cy="40" r="32" fill="none" stroke="#e0e0e0" strokeWidth="1" />

            {/* Cardinal directions */}
            <text x="40" y="15" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#555">N</text>
            <text x="65" y="43" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#555">E</text>
            <text x="40" y="68" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#555">S</text>
            <text x="15" y="43" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#555">W</text>

            {/* Wind direction arrow */}
            <g style={{ transform: `rotate(${animatedAngle}deg)`, transformOrigin: '40px 40px', transition: 'transform 1s ease-in-out' }}>
              <path d="M 40 20 L 43 38 L 40 36 L 37 38 Z" fill={color} />
              <line x1="40" y1="38" x2="40" y2="60" stroke={color} strokeWidth="2" />
            </g>

            <circle cx="40" cy="40" r="4" fill={color} />
          </svg>
        </div>

        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '20px', fontWeight: 'bold', color: color, lineHeight: 1 }}>
            {getCardinalDirection(degrees)}
          </div>
          <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
            {degrees.toFixed(0)}°
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      marginTop: '12px',
      marginBottom: '12px',
      padding: '16px',
      backgroundColor: '#f5f7f5',
      borderRadius: '8px',
      border: '1px solid #e8e8e8'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
        <div style={{ position: 'relative', width: '100px', height: '100px', flexShrink: 0 }}>
          <svg width="100" height="100" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
            {/* Compass circle */}
            <circle cx="50" cy="50" r="45" fill="white" stroke="#555" strokeWidth="2" />
            <circle cx="50" cy="50" r="40" fill="none" stroke="#e0e0e0" strokeWidth="1" />

            {/* Cardinal directions */}
            <text x="50" y="15" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#555">N</text>
            <text x="85" y="53" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#555">E</text>
            <text x="50" y="88" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#555">S</text>
            <text x="15" y="53" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#555">W</text>

            {/* Wind direction arrow */}
            <g style={{ transform: `rotate(${animatedAngle}deg)`, transformOrigin: '50px 50px', transition: 'transform 1s ease-in-out' }}>
              <path d="M 50 15 L 54 45 L 50 42 L 46 45 Z" fill={color} />
              <line x1="50" y1="45" x2="50" y2="75" stroke={color} strokeWidth="3" />
            </g>

            <circle cx="50" cy="50" r="5" fill={color} />
          </svg>
        </div>

        <div style={{ flex: 1 }}>
          <Text strong style={{ fontSize: '12px', display: 'block', color: '#666' }}>
            Wind Direction
          </Text>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginTop: '4px' }}>
            <Text strong style={{ fontSize: '28px', color: color, lineHeight: 1 }}>
              {getCardinalDirection(degrees)}
            </Text>
            <Text style={{ fontSize: '16px', color: '#666' }}>({degrees.toFixed(0)}°)</Text>
          </div>
          {windData.time && (
            <Text type="secondary" style={{ fontSize: '10px', display: 'block', marginTop: '8px' }}>
              Last updated: {windData.time}
            </Text>
          )}
        </div>
      </div>
    </div>
  );
};

// Vent Position Display - Slider style
export const VentPositionDisplay = ({ sensor, compact = false }) => {
  const [animatedPosition, setAnimatedPosition] = useState(0);

  const ventData = getLastKnownValue(sensor, 'vent_position');
  const position = ventData ? parseFloat(ventData.value) : 0;

  useEffect(() => {
    if (!ventData) return;
    const timeout = setTimeout(() => setAnimatedPosition(position), 150);
    return () => clearTimeout(timeout);
  }, [position, ventData]);

  if (!ventData) return null;

  const color = '#0E4128';

  if (compact) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        gap: '8px'
      }}>
        <div style={{ position: 'relative', width: '60px', height: '80px' }}>
          <svg width="60" height="80" viewBox="0 0 60 80" xmlns="http://www.w3.org/2000/svg">
            {/* Track */}
            <rect x="25" y="10" width="10" height="60" rx="5" fill="#e0e0e0" />

            {/* Filled portion */}
            <rect
              x="25"
              y={70 - (60 * animatedPosition / 100)}
              width="10"
              height={60 * animatedPosition / 100}
              rx="5"
              fill={color}
              style={{ transition: 'all 1s ease-in-out' }}
            />

            {/* Slider handle */}
            <rect
              x="20"
              y={67 - (60 * animatedPosition / 100)}
              width="20"
              height="6"
              rx="3"
              fill={color}
              stroke="white"
              strokeWidth="1"
              style={{ transition: 'all 1s ease-in-out' }}
            />

            {/* Tick marks */}
            {[0, 25, 50, 75, 100].map((mark) => (
              <line
                key={mark}
                x1="37"
                y1={70 - (60 * mark / 100)}
                x2="42"
                y2={70 - (60 * mark / 100)}
                stroke="#999"
                strokeWidth="1"
              />
            ))}
          </svg>
        </div>

        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: color, lineHeight: 1 }}>
            {position.toFixed(0)}
            <span style={{ fontSize: '14px', marginLeft: '2px' }}>%</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      marginTop: '12px',
      marginBottom: '12px',
      padding: '16px',
      backgroundColor: '#f5f7f5',
      borderRadius: '8px',
      border: '1px solid #e8e8e8'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
        <div style={{ position: 'relative', width: '80px', height: '100px', flexShrink: 0 }}>
          <svg width="80" height="100" viewBox="0 0 80 100" xmlns="http://www.w3.org/2000/svg">
            {/* Track */}
            <rect x="32" y="10" width="16" height="80" rx="8" fill="#e0e0e0" />

            {/* Filled portion */}
            <rect
              x="32"
              y={90 - (80 * animatedPosition / 100)}
              width="16"
              height={80 * animatedPosition / 100}
              rx="8"
              fill={color}
              style={{ transition: 'all 1s ease-in-out' }}
            />

            {/* Slider handle */}
            <rect
              x="25"
              y={87 - (80 * animatedPosition / 100)}
              width="30"
              height="8"
              rx="4"
              fill={color}
              stroke="white"
              strokeWidth="2"
              style={{ transition: 'all 1s ease-in-out' }}
            />

            {/* Tick marks */}
            {[0, 25, 50, 75, 100].map((mark) => (
              <line
                key={mark}
                x1="50"
                y1={90 - (80 * mark / 100)}
                x2="58"
                y2={90 - (80 * mark / 100)}
                stroke="#999"
                strokeWidth="1.5"
              />
            ))}
          </svg>
        </div>

        <div style={{ flex: 1 }}>
          <Text strong style={{ fontSize: '12px', display: 'block', color: '#666' }}>
            Vent Position
          </Text>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px', marginTop: '4px' }}>
            <Text strong style={{ fontSize: '28px', color: color, lineHeight: 1 }}>
              {position.toFixed(0)}
            </Text>
            <Text style={{ fontSize: '16px', color: color }}>%</Text>
          </div>
          {ventData.time && (
            <Text type="secondary" style={{ fontSize: '10px', display: 'block', marginTop: '8px' }}>
              Last updated: {ventData.time}
            </Text>
          )}
        </div>
      </div>
    </div>
  );
};

// Light Display - Sun icon with rays
export const LightDisplay = ({ sensor, compact = false }) => {
  const [animatedIntensity, setAnimatedIntensity] = useState(0);

  const lightData = getLastKnownValue(sensor, 'light');
  const lux = lightData ? parseFloat(lightData.value) : 0;
  const maxLux = 100000;
  const percentage = Math.max(0, Math.min(100, (lux / maxLux) * 100));

  useEffect(() => {
    if (!lightData) return;
    const timeout = setTimeout(() => setAnimatedIntensity(percentage), 150);
    return () => clearTimeout(timeout);
  }, [percentage, lightData]);

  if (!lightData) return null;

  const color = '#FDB813';
  const rayOpacity = 0.2 + (animatedIntensity / 100) * 0.8;

  if (compact) {
    // Format lux value
    const formatLux = (value) => {
      if (value >= 1000) return `${(value / 1000).toFixed(1)}k`;
      return value.toFixed(0);
    };

    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        gap: '8px'
      }}>
        <svg width="80" height="80" viewBox="0 0 80 80" xmlns="http://www.w3.org/2000/svg">
          {/* Sun rays */}
          {[0, 45, 90, 135, 180, 225, 270, 315].map((angle) => (
            <line
              key={angle}
              x1="40"
              y1="40"
              x2={40 + Math.cos((angle - 90) * Math.PI / 180) * 32}
              y2={40 + Math.sin((angle - 90) * Math.PI / 180) * 32}
              stroke={color}
              strokeWidth="3"
              strokeLinecap="round"
              opacity={rayOpacity}
              style={{ transition: 'opacity 1s ease-in-out' }}
            />
          ))}

          {/* Sun circle */}
          <circle cx="40" cy="40" r="18" fill={color} opacity={0.9} />
          <circle cx="40" cy="40" r="15" fill={color} />
        </svg>

        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '20px', fontWeight: 'bold', color: color, lineHeight: 1 }}>
            {formatLux(lux)}
            <span style={{ fontSize: '12px', marginLeft: '2px' }}>lux</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      marginTop: '12px',
      marginBottom: '12px',
      padding: '16px',
      backgroundColor: '#f5f7f5',
      borderRadius: '8px',
      border: '1px solid #e8e8e8'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
        <div style={{ position: 'relative', width: '100px', height: '100px', flexShrink: 0 }}>
          <svg width="100" height="100" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
            {/* Sun rays */}
            {[0, 45, 90, 135, 180, 225, 270, 315].map((angle) => (
              <line
                key={angle}
                x1="50"
                y1="50"
                x2={50 + Math.cos((angle - 90) * Math.PI / 180) * 40}
                y2={50 + Math.sin((angle - 90) * Math.PI / 180) * 40}
                stroke={color}
                strokeWidth="4"
                strokeLinecap="round"
                opacity={rayOpacity}
                style={{ transition: 'opacity 1s ease-in-out' }}
              />
            ))}

            {/* Sun circle */}
            <circle cx="50" cy="50" r="22" fill={color} opacity={0.9} />
            <circle cx="50" cy="50" r="18" fill={color} />
          </svg>
        </div>

        <div style={{ flex: 1 }}>
          <Text strong style={{ fontSize: '12px', display: 'block', color: '#666' }}>
            Light Intensity
          </Text>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px', marginTop: '4px' }}>
            <Text strong style={{ fontSize: '28px', color: color, lineHeight: 1 }}>
              {lux >= 1000 ? (lux / 1000).toFixed(1) + 'k' : lux.toFixed(0)}
            </Text>
            <Text style={{ fontSize: '14px', color: color }}>lux</Text>
          </div>
          {lightData.time && (
            <Text type="secondary" style={{ fontSize: '10px', display: 'block', marginTop: '8px' }}>
              Last updated: {lightData.time}
            </Text>
          )}
        </div>
      </div>
    </div>
  );
};

// Door Status Display — derives today's count from already-loaded timeline data
// Uses shared utility to avoid N+1 API calls on the list page
export const DoorStatusDisplay = ({ sensor, compact = false, timezone }) => {
  const todayOpens = useMemo(() => {
    const todayStart = timezone ? moment().tz(timezone).startOf('day') : moment().startOf('day');
    const allEntries = extractPulseEntries(sensor.timelines);
    const todayEntries = allEntries.filter(e => e.momentTime.isSameOrAfter(todayStart));
    return calculateTotalDelta(todayEntries);
  }, [sensor.timelines, timezone]);

  const lastPulse = getLastKnownValue(sensor, 'total_pulses');

  if (compact) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 0' }}>
        <Text strong style={{ fontSize: '14px' }}>Today's Counter: {todayOpens}</Text>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '16px' }}>
      <Text type="secondary" style={{ fontSize: '11px', marginBottom: '4px' }}>Today's Counter</Text>
      <Text strong style={{ fontSize: '28px', color: '#0B2A1B' }}>{todayOpens}</Text>

      {lastPulse && lastPulse.time && (
        <Text type="secondary" style={{ fontSize: '10px', display: 'block', marginTop: '8px' }}>
          Last updated: {lastPulse.time}
        </Text>
      )}
    </div>
  );
};

/**
 * MeterReadingDisplay — card display for the Dragino AIS01-LB AI meter reader.
 * Shows the latest cumulative meter reading (the value the user wants to see).
 */
export const MeterReadingDisplay = ({ sensor, compact = false }) => {
  const lastReading = getLastKnownValue(sensor, 'meter_reading');
  const value = lastReading ? lastReading.value : '-';
  const unit = lastReading && lastReading.unit ? lastReading.unit : '';

  if (compact) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 0' }}>
        <Text strong style={{ fontSize: '14px' }}>Reading: {value}{unit ? ` ${unit}` : ''}</Text>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '16px' }}>
      <Text type="secondary" style={{ fontSize: '11px', marginBottom: '4px' }}>Meter Reading</Text>
      <Text strong style={{ fontSize: '28px', color: '#0B2A1B' }}>{value}{unit ? ` ${unit}` : ''}</Text>

      {lastReading && lastReading.time && (
        <Text type="secondary" style={{ fontSize: '10px', display: 'block', marginTop: '8px' }}>
          Last updated: {lastReading.time}
        </Text>
      )}
    </div>
  );
};

/**
 * WaterMeterDisplay — card display for the CPL03 wired as a water-meter
 * pulse counter (part_number = DOOR_CONTACT_SENSOR_WATER). Shows the
 * cumulative TOTAL volume (not today's delta — admins want "the meter
 * reads X m³" as the at-a-glance number, matching what a mechanical meter
 * face would show). Uses the per-sensor `litres_per_pulse` configuration
 * to convert pulses to m³, and a water-drop icon to visually distinguish
 * from the door counter.
 *
 * The cumulative pulse value comes from the latest total_pulses timeline
 * entry. The backend already folds in any per-sensor pulse-calibration
 * offset before returning it (SensorService::applyPulseOffset), so this
 * number reflects the calibrated total rather than the device's raw
 * internal counter.
 *
 * Conversion: m³ = total_pulses × litres_per_pulse / 1000.
 */
export const WaterDropIcon = ({ size = 22, color = '#1E90C2' }) => (
  // Inline SVG — avoids pulling in a new icon dependency. Drop shape with a
  // subtle highlight; sized by the `size` prop to stay crisp on retina.
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
    <path
      d="M12 2.5 C7.5 9 5 12.5 5 15.5 a7 7 0 1 0 14 0 c0-3-2.5-6.5-7-13z"
      fill={color}
    />
    <ellipse cx="10" cy="14.5" rx="1.6" ry="3" fill="white" opacity="0.55" />
  </svg>
);

// eslint-disable-next-line no-unused-vars
export const WaterMeterDisplay = ({ sensor, compact = false, timezone }) => {
  // Default to 1 L/pulse (the most common residential meter); admin sets the
  // exact ratio per-sensor via SensorModal when adding a DOOR_CONTACT_SENSOR_WATER.
  const litresPerPulse = Number(sensor.litres_per_pulse) > 0
    ? Number(sensor.litres_per_pulse)
    : 1;

  // Cumulative total straight off the latest timeline entry — no per-day
  // windowing. `timezone` is accepted for API symmetry with other display
  // components but isn't used here (the value is timezone-independent).
  const lastPulse = getLastKnownValue(sensor, 'total_pulses');
  const totalPulses = lastPulse ? parseFloat(lastPulse.value) : 0;
  const safePulses = Number.isFinite(totalPulses) ? totalPulses : 0;
  const totalM3 = (safePulses * litresPerPulse) / 1000;
  // Cap at 3 decimals (enough resolution for 1 L = 0.001 m³) but DON'T pad
  // with trailing zeros — 0 stays "0", 180.000 → "180", 1234.5 → "1234.5",
  // so the card never shows a `0.000` placeholder.
  const totalM3Display = String(Number(totalM3.toFixed(3)));

  if (compact) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 0' }}>
        <WaterDropIcon size={16} />
        <Text strong style={{ fontSize: '14px' }}>Total Volume: {totalM3Display} m³</Text>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
        <WaterDropIcon size={18} />
        <Text type="secondary" style={{ fontSize: '11px' }}>Total Volume</Text>
      </div>
      <Text strong style={{ fontSize: '28px', color: '#0B2A1B' }}>
        {totalM3Display} <span style={{ fontSize: '16px' }}>m³</span>
      </Text>

      {lastPulse && lastPulse.time && (
        <Text type="secondary" style={{ fontSize: '10px', display: 'block', marginTop: '8px' }}>
          Last updated: {lastPulse.time}
        </Text>
      )}
    </div>
  );
};

// Export the generic gauge for use with other sensor types
export { GenericGaugeDisplay };
